import { api } from './client';

export interface LoginResponse {
  userId: number;
  email: string;
  tenantId: number;
  activeSpaceId: number | null;
}

export interface MeResponse {
  userId: number;
  tenantId: number;
  activeSpaceId: number | null;
  activeSpaceSlug: string | null;
  activeSpaceSubdomain: string | null;
  partnerId: number | null;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  mattermostUserId: string | null;
  avatarUrl: string | null;
  roleKeys: string[];
  permissionKeys: string[];
  accessibleSpaceIds: number[];
  // Efektivní per-space modulový přístup (katalogové menu-klíče). Frontend z
  // toho filtruje horní menu. Nepřítomné = stará session/backend → menu se
  // nefiltruje (fail-open na UI; backend gate platí vždy).
  moduleAccess?: {
    spaceId: number;
    spaceSlug: string;
    modules: { moduleKey: string; level: 'read' | 'write' }[];
    permissionKeys?: string[];
  }[];
  canAccessAllSpacesInTenant?: boolean;
  mfaEnabled?: boolean;
  mfaEnforced?: boolean;
}

export interface SpaceListItem {
  id: number;
  slug: string;
  name: string;
  subdomain: string | null;
  status: 'active' | 'suspended' | 'archived';
}

export interface MfaRequiredResponse {
  mfaRequired: true;
  mode: 'verify' | 'enroll';
}

export type LoginResult = LoginResponse | MfaRequiredResponse;

export function isMfaRequired(r: LoginResult): r is MfaRequiredResponse {
  return (r as MfaRequiredResponse).mfaRequired === true;
}

export function login(email: string, password: string): Promise<LoginResult> {
  return api.post<LoginResult>('/api/v1/auth/login', { email, password });
}

export interface MfaStatus {
  enabled: boolean;
  enforced: boolean;
  backupCodesLeft: number;
}

export interface MfaEnrollmentStart {
  secret: string;
  otpauthUrl: string;
  qrSvg: string;
}

export function mfaStatus(): Promise<MfaStatus> {
  return api.get<MfaStatus>('/api/v1/auth/mfa/status');
}

export function mfaEnrollStart(): Promise<MfaEnrollmentStart> {
  return api.post<MfaEnrollmentStart>('/api/v1/auth/mfa/enroll/start');
}

export function mfaEnrollConfirm(code: string): Promise<{ backupCodes: string[]; session?: LoginResponse }> {
  return api.post('/api/v1/auth/mfa/enroll/confirm', { code });
}

export function mfaVerify(code: string): Promise<{ session: LoginResponse; method: 'totp' | 'backup'; backupCodesLeft: number }> {
  return api.post('/api/v1/auth/mfa/verify', { code });
}

export function mfaRegenerateBackupCodes(code: string): Promise<{ backupCodes: string[] }> {
  return api.post('/api/v1/auth/mfa/backup-codes/regenerate', { code });
}

export function mfaStepUp(code: string): Promise<{ verified: boolean }> {
  return api.post('/api/v1/auth/mfa/step-up', { code });
}

export interface MobileDevice {
  id: number;
  name: string;
  platform: 'ios' | 'android';
  appVersion: string | null;
  createdAt: string;
  lastSeenAt: string | null;
  current: boolean;
}

export function mobileDevices(): Promise<MobileDevice[]> {
  return api.get<MobileDevice[]>('/api/v1/auth/mobile/devices');
}

export function revokeMobileDevice(id: number): Promise<unknown> {
  return api.post(`/api/v1/auth/mobile/devices/${id}/revoke`);
}

export function logout(): Promise<unknown> {
  return api.post('/api/v1/auth/logout');
}

export function getMe(): Promise<MeResponse> {
  return api.get<MeResponse>('/api/v1/me');
}

export function uploadMyAvatar(contentBase64: string, mime: string): Promise<{ avatarUrl: string | null }> {
  return api.post('/api/v1/me/avatar', { contentBase64, mime });
}

export function deleteMyAvatar(): Promise<{ avatarUrl: string | null }> {
  return api.del('/api/v1/me/avatar');
}

export function updateMe(body: {
  firstName?: string | null;
  lastName?: string | null;
  mattermostUserId?: string | null;
}): Promise<{ updated: boolean }> {
  return api.put<{ updated: boolean }>('/api/v1/me', body);
}

// Self-service změna vlastního hesla. Vyžaduje současné heslo (server ho ověří
// argon2) + nové. Vrací { updated: true } při úspěchu, jinak ApiError s kódem
// PASSWORD_TOO_SHORT / INVALID_CURRENT_PASSWORD.
export function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ updated: boolean }> {
  return api.put<{ updated: boolean }>('/api/v1/me/password', { currentPassword, newPassword });
}

export function listSpaces(): Promise<SpaceListItem[]> {
  return api.get<SpaceListItem[]>('/api/v1/spaces');
}

export function getSpace(id: number): Promise<SpaceListItem> {
  return api.get<SpaceListItem>(`/api/v1/spaces/${id}`);
}

export function requestPasswordReset(email: string): Promise<{ sent: boolean }> {
  return api.post('/api/v1/auth/password-reset/request', { email });
}

export function validatePasswordReset(token: string): Promise<{ valid: boolean; mfaRequired: boolean }> {
  return api.get(`/api/v1/auth/password-reset/validate?t=${encodeURIComponent(token)}`);
}

export function confirmPasswordReset(token: string, newPassword: string, mfaCode?: string): Promise<{ updated: boolean }> {
  return api.post('/api/v1/auth/password-reset/confirm', { token, newPassword, mfaCode: mfaCode || undefined });
}
